import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Grid,
  Paper,
  Collapse,
  Button,
} from "@mui/material";
import VaccinesIcon from "@mui/icons-material/Vaccines";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import GlassCard from "../../../components/common/GlassCard";

function getStatusColor(status) {
  switch (status?.toLowerCase()) {
    case "completed":
      return "success";
    case "pending":
      return "warning";
    case "declined":
      return "error";
    default:
      return "default";
  }
}

function ImmunizationsCard({
  immunizations,
  onAddImmunization,
  onDeleteImmunization,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeImmunizationId, setActiveImmunizationId] = useState(null);
  const [expandAll, setExpandAll] = useState(false);

  const handleMenuOpen = (event, id) => {
    setMenuAnchor(event.currentTarget);
    setActiveImmunizationId(id);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setActiveImmunizationId(null);
  };

  const handleDelete = (immunizationId) => {
    handleMenuClose();
    if (onDeleteImmunization) {
      onDeleteImmunization(immunizationId);
    }
  };

  const sortedImmunizations = immunizations
    ? [...immunizations].sort((a, b) => {
        const dateA = new Date(a.administration_date || 0);
        const dateB = new Date(b.administration_date || 0);
        return dateB - dateA; // Most recent first
      })
    : [];

  // Count vaccines by status
  const completedCount = sortedImmunizations.filter(
    (i) => i.status?.toLowerCase() === "completed"
  ).length;
  const pendingCount = sortedImmunizations.filter(
    (i) => i.status?.toLowerCase() === "pending"
  ).length;

  return (
    <GlassCard
      title="Immunizations"
      addLabel="Add Immunization"
      icon={<VaccinesIcon color="primary" />}
      onAdd={onAddImmunization}
      data-testid="immunizations-card"
      data-card-category="immunizations"
    >
      {/* Summary */}
      {sortedImmunizations?.length > 0 && (
        <Grid container spacing={1} mb={2} sx={{ p: 1, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
          <Grid item xs={6}>
            <Typography
              variant="caption"
              color="success.dark"
              fontWeight={600}
              data-testid="immunizations-completed-count"
              data-field-name="completed_count"
            >
              Completed: {completedCount}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="caption"
              color="warning.dark"
              fontWeight={600}
              data-testid="immunizations-pending-count"
              data-field-name="pending_count"
            >
              Pending: {pendingCount}
            </Typography>
          </Grid>
        </Grid>
      )}

      {sortedImmunizations?.length ? (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
            {sortedImmunizations.slice(0, 2).map((immunization) => (
              <Paper
                key={immunization.id}
                sx={{
                  p: 1.25,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
                  data-testid={`immunization-item-${immunization.id}`}
                  data-vaccine-name={immunization.vaccine_name}
                  data-immunization-status={immunization.status}
                  data-administration-date={immunization.administration_date}
                  data-cvx-code={immunization.cvx_code}
                  data-provider={immunization.provider}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                    <Box flex={1}>
                      <Typography variant="body2" fontWeight={600}>
                        {immunization.vaccine_name || "Vaccine"} • {immunization.administration_date
                          ? new Date(immunization.administration_date).toLocaleDateString()
                          : "N/A"}
                      </Typography>
                      {immunization.provider && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {immunization.provider}
                        </Typography>
                      )}
                    </Box>
                    <Box display="flex" gap={0.5} alignItems="center">
                      <Chip
                        size="small"
                        label={immunization.status || "pending"}
                        color={getStatusColor(immunization.status)}
                        variant="outlined"
                        data-testid={`immunization-status-chip-${immunization.id}`}
                        data-field-name="status"
                      />
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, immunization.id)}
                          data-testid={`immunization-menu-${immunization.id}`}
                          aria-label="Immunization options menu"
                        >
                          <MoreVertIcon aria-label="Immunization options menu" fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
              </Paper>
            ))}
          </Box>

          {sortedImmunizations?.length > 2 && (
            <>
              <Collapse in={expandAll} timeout="auto" unmountOnExit>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
                  {sortedImmunizations.slice(2).map((immunization) => (
                    <Paper
                      key={immunization.id}
                      sx={{
                        p: 1.25,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                      }}
                        data-testid={`immunization-item-${immunization.id}`}
                        data-vaccine-name={immunization.vaccine_name}
                        data-immunization-status={immunization.status}
                        data-administration-date={immunization.administration_date}
                        data-cvx-code={immunization.cvx_code}
                        data-provider={immunization.provider}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                          <Box flex={1}>
                            <Typography variant="body2" fontWeight={600}>
                              {immunization.vaccine_name || "Vaccine"} • {immunization.administration_date
                                ? new Date(immunization.administration_date).toLocaleDateString()
                                : "N/A"}
                            </Typography>
                            {immunization.provider && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {immunization.provider}
                              </Typography>
                            )}
                          </Box>
                          <Box display="flex" gap={0.5} alignItems="center">
                            <Chip
                              size="small"
                              label={immunization.status || "pending"}
                              color={getStatusColor(immunization.status)}
                              variant="outlined"
                              data-testid={`immunization-status-chip-${immunization.id}`}
                              data-field-name="status"
                            />
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={(e) => handleMenuOpen(e, immunization.id)}
                                data-testid={`immunization-menu-${immunization.id}`}
                                aria-label="Immunization options menu"
                              >
                                <MoreVertIcon aria-label="Immunization options menu" fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                    </Paper>
                  ))}
                </Box>
              </Collapse>

              <Button
                size="small"
                onClick={() => setExpandAll(!expandAll)}
                startIcon={expandAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ textTransform: "none", mt: 1 }}
                data-testid="immunizations-view-all-button"
                data-action="expand"
              >
                {expandAll ? "View Less" : `View All (${sortedImmunizations.length - 2} more)`}
              </Button>
            </>
          )}
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No immunizations recorded
        </Typography>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => handleDelete(activeImmunizationId)}
          data-testid={`immunization-menu-delete-${activeImmunizationId}`}
          data-action="delete"
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </GlassCard>
  );
}

ImmunizationsCard.propTypes = {
  immunizations: PropTypes.array,
  onAddImmunization: PropTypes.func,
  onDeleteImmunization: PropTypes.func,
};

ImmunizationsCard.defaultProps = {
  immunizations: [],
  onAddImmunization: null,
  onDeleteImmunization: null,
};

export default ImmunizationsCard;
